/**
 * Client Dashboard search: a box above the fleet table that narrows it to
 * the clients whose name or website matches what you type, fuzzily ("mke"
 * finds mikehammermoving.com; see lib/fuzzyMatch.ts).
 *
 * Fleet rows only carry `tenant_id`, which is the client's website. The
 * client's name comes from the workspace list the app already holds
 * (workspace.website maps to tenant_id), so "mike hammer" finds
 * mikehammermoving.com without a new request.
 *
 * The text lives in the URL (?q=) and nowhere else, so a reload or a shared
 * link keeps the same view. It is combined with the table's own filters and
 * sort, never replacing them.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { fuzzyFilter, fuzzyMatches } from '@/lib/fuzzyMatch';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

export const CLIENT_SEARCH_PARAM = 'q';

/** Lowercased, trimmed, inner runs of spaces collapsed. A pasted URL is
 *  cut down to its host, so "https://www.aaafence.com/" finds aaafence.com. */
export function normalizeClientQuery(raw: string | null | undefined): string {
  return (raw ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');
}

/** True when `query` is empty or fuzzily matches the name or website
 *  ("mke" finds mikehammermoving.com; see lib/fuzzyMatch). */
export function matchesClient(
  query: string,
  client: { website: string; name?: string | null },
): boolean {
  return fuzzyMatches(query, [client.website, client.name]);
}

/** The search text from the URL, and a setter that keeps every other param. */
export function useClientSearchParam(): [string, (next: string) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(CLIENT_SEARCH_PARAM) ?? '';
  const setValue = useCallback(
    (next: string) => {
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (next) out.set(CLIENT_SEARCH_PARAM, next);
          else out.delete(CLIENT_SEARCH_PARAM);
          return out;
        },
        // Typing should not add a history entry per keystroke.
        { replace: true },
      );
    },
    [setParams],
  );
  return [value, setValue];
}

/** website (lowercased) → client name, from the workspaces already loaded. */
export function useClientNamesByWebsite(): Map<string, string> {
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  return useMemo(() => {
    const out = new Map<string, string>();
    for (const ws of workspaces ?? []) {
      const site = ws.website?.trim().toLowerCase();
      if (site && ws.name) out.set(site, ws.name);
    }
    return out;
  }, [workspaces]);
}

/** Rows whose name or website fuzzily matches the text; all rows when it is
 *  empty. `sort` puts the best match first; the Client Dashboard leaves it
 *  off because its table is sorted by the column the user picked. */
export function filterClientsByQuery<T>(
  rows: readonly T[],
  rawQuery: string,
  websiteOf: (row: T) => string,
  names: Map<string, string>,
  { sort = false }: { sort?: boolean } = {},
): T[] {
  const query = normalizeClientQuery(rawQuery);
  if (!query) return [...rows];
  return fuzzyFilter(
    rows,
    query,
    (row) => {
      const website = websiteOf(row);
      return [website, names.get(website.trim().toLowerCase())];
    },
    { sort },
  );
}

export function ClientSearchBox({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="relative w-full max-w-xs">
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search by client name or website"
        aria-label="Search clients by name or website"
        className="h-8 pl-8 pr-8 text-sm"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

export function ClientSearchEmpty({
  query,
  onClear,
}: {
  query: string;
  onClear: () => void;
}) {
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-3 rounded border border-dashed border-border p-4 text-sm text-muted-foreground"
    >
      <span>No clients match "{query.trim()}"</span>
      <Button type="button" variant="outline" size="sm" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}
